import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiCheck, FiCreditCard, FiTrendingUp, FiUsers, FiX } from 'react-icons/fi';
import '../../../css/auth-pages.css';

const MotionDiv = motion.div;
const MotionButton = motion.button;

const ThankYou = ({ isOpen, onClose, paymentData, planData, onNavigateToDashboard }) => {
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      
      // Step animation sequence
      const stepTimer1 = setTimeout(() => setCurrentStep(1), 500);
      const stepTimer2 = setTimeout(() => setCurrentStep(2), 1000);
      const stepTimer3 = setTimeout(() => setCurrentStep(3), 1500);
      
      // Auto close after 5 seconds if no action
      const autoClose = setTimeout(() => {
        if (onNavigateToDashboard) {
          onNavigateToDashboard();
        }
      }, 5000);

      return () => {
        clearTimeout(stepTimer1);
        clearTimeout(stepTimer2);
        clearTimeout(stepTimer3);
        clearTimeout(autoClose);
      };
    }
  }, [isOpen, onNavigateToDashboard]);

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    }
  };

  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.8,
      y: 20
    },
    visible: { 
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { 
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };

  const checkmarkVariants = {
    hidden: { 
      scale: 0,
      rotate: -180
    },
    visible: { 
      scale: 1,
      rotate: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  const stepVariants = {
    hidden: { 
      opacity: 0,
      x: -20
    },
    visible: { 
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <MotionDiv  
        className="beta-modal-backdrop thankyou-backdrop"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        onClick={onClose}
      >
         {/* Confetti Background */}
        {showConfetti && (
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <MotionDiv 
                key={`confetti-${i}`}
                className={`confetti confetti-${i % 4}`}
                initial={{
                  opacity: 0,
                  y: -100,
                  x: Math.random() * window.innerWidth,
                  rotate: 0
                }}
                animate={{
                  opacity: [0, 1, 1, 0],
                  y: window.innerHeight + 100,
                  rotate: 360 * (Math.random() > 0.5 ? 1 : -1)
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  delay: Math.random() * 2,
                  ease: "easeOut"
                }}
              />
            ))}
          </div>
        )}

        <MotionDiv  
          className="beta-modal thankyou-modal"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button 
            className="modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX />
          </button>

          {/* Success Animation */}
          <div className="success-animation">
            <MotionDiv  
              className="success-checkmark"
              variants={checkmarkVariants}
              initial="hidden"
              animate="visible"
            >
              <FiCheck />
            </MotionDiv >
            
            <div className="success-rings">
              <MotionDiv  
                className="ring ring-1"
                animate={{
                  scale: [1, 1.5, 1.8],
                  opacity: [0.8, 0.4, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut"
                }}
              />
              <MotionDiv  
                className="ring ring-2"
                animate={{
                  scale: [1, 1.3, 1.6],
                  opacity: [0.6, 0.3, 0]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: 0.3
                }}
              />
            </div>
          </div>

          {/* Content */}
          <div className="thankyou-content">
            <motion.h2 
              className="thankyou-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              Payment Successful! 🎉
            </motion.h2>
            
            <motion.p 
              className="thankyou-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              Welcome to POSlah! Your journey to smarter business management starts now.
            </motion.p>

            {/* Payment Summary */}
            <MotionDiv  
              className="payment-summary"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
            >
              <div className="summary-header">
                <FiCreditCard />
                <span>Payment Details</span>
              </div>
              
              <div className="summary-details">
                <div className="detail-row">
                  <span>Plan:</span>
                  <span className="highlight">{planData?.plan_name || 'Pro Plan'}</span>
                </div>
                <div className="detail-row">
                  <span>Amount:</span>
                  <span className="highlight">${paymentData?.amount || '59.99'}/month</span>
                </div>
                <div className="detail-row">
                  <span>Transaction ID:</span>
                  <span className="transaction-id">{paymentData?.transactionId || 'TXN_123456789'}</span>
                </div>
                {planData?.trial_days > 0 && (
                  <div className="detail-row trial-info">
                    <span>Trial Period:</span>
                    <span className="highlight">{planData.trial_days} days free</span>
                  </div>
                )}
              </div>
            </MotionDiv >

            {/* Next Steps */}
            <MotionDiv  
              className="next-steps"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.6 }}
            >
              <h3>What happens next?</h3>
              
              <div className="steps-list">
                <MotionDiv  
                  className={`step-item ${currentStep >= 1 ? 'completed' : ''}`}
                  variants={stepVariants}
                  initial="hidden"
                  animate={currentStep >= 1 ? "visible" : "hidden"}
                >
                  <div className="step-icon">
                    <FiCheck />
                  </div>
                  <div className="step-content">
                    <h4>Account Setup Complete</h4>
                    <p>Your POSlah account has been created and activated</p>
                  </div>
                </MotionDiv >

                <MotionDiv  
                  className={`step-item ${currentStep >= 2 ? 'completed' : ''}`}
                  variants={stepVariants}
                  initial="hidden"
                  animate={currentStep >= 2 ? "visible" : "hidden"}
                >
                  <div className="step-icon">
                    <FiTrendingUp />
                  </div>
                  <div className="step-content">
                    <h4>Dashboard Ready</h4>
                    <p>Access all your business tools and analytics</p>
                  </div>
                </MotionDiv >

                <MotionDiv  
                  className={`step-item ${currentStep >= 3 ? 'completed' : ''}`}
                  variants={stepVariants}
                  initial="hidden"
                  animate={currentStep >= 3 ? "visible" : "hidden"}
                >
                  <div className="step-icon">
                    <FiUsers />
                  </div>
                  <div className="step-content">
                    <h4>Support Available</h4>
                    <p>Our team is ready to help you get started</p>
                  </div>
                </MotionDiv >
              </div>
            </MotionDiv >

            {/* Action Buttons */}
            <MotionDiv  
              className="thankyou-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.6 }}
            >
              <MotionButton 
                className="btn-primary dashboard-btn"
                onClick={onNavigateToDashboard}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                Go to Dashboard
              </MotionButton>
              
              <MotionButton 
                className="btn-secondary"
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Close
              </MotionButton>
            </MotionDiv >

            {/* Auto-redirect notice */}
            <motion.p 
              className="auto-redirect-notice"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2, duration: 0.6 }}
            >
              Redirecting to dashboard in a few seconds...
            </motion.p>
          </div>
        </MotionDiv >
      </MotionDiv >

      <style>{`
        .thankyou-backdrop {
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(10px);
        }

        .confetti-container {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 1;
        }

        .confetti {
          position: absolute;
          width: 8px;
          height: 8px;
          pointer-events: none;
        }

        .confetti-0 { background: var(--cyberpunk-pink); }
        .confetti-1 { background: var(--cyberpunk-blue); }
        .confetti-2 { background: #ffd700; }
        .confetti-3 { background: #34c759; }

        .thankyou-modal {
          max-width: 600px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          background: var(--glass-bg);
          backdrop-filter: blur(20px);
          border: 1px solid var(--glass-border);
          border-radius: 24px;
          padding: 3rem;
          position: relative;
          z-index: 10;
        }

        .modal-close-btn {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid var(--glass-border);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-light);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .modal-close-btn:hover {
          background: var(--cyberpunk-pink);
          color: white;
          transform: rotate(90deg);
        }

        .success-animation {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-bottom: 2rem;
          position: relative;
        }

        .success-checkmark {
          width: 80px;
          height: 80px;
          background: var(--accent-gradient);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          color: white;
          position: relative;
          z-index: 2;
          box-shadow: 0 10px 30px rgba(255, 107, 157, 0.4);
        }

        .success-rings {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .ring {
          position: absolute;
          width: 80px;
          height: 80px;
          border: 2px solid var(--cyberpunk-blue);
          border-radius: 50%;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .ring-2 {
          border-color: var(--cyberpunk-pink);
        }

        .thankyou-content {
          text-align: center;
        }

        .thankyou-title {
          font-family: 'Orbitron', monospace;
          font-size: 2rem;
          font-weight: 700;
          background: var(--cotton-candy);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          margin-bottom: 1rem;
        }

        .thankyou-subtitle {
          color: var(--text-light);
          font-size: 1.1rem;
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .payment-summary {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--glass-border);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          text-align: left;
        }

        .summary-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: var(--cyberpunk-blue);
          font-weight: 600;
          margin-bottom: 1rem;
          font-size: 1.1rem;
        }

        .summary-details {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          color: var(--text-light);
        }

        .detail-row .highlight {
          color: var(--text-white);
          font-weight: 600;
        }

        .transaction-id {
          font-family: monospace;
          font-size: 0.9rem;
          color: var(--cyberpunk-blue);
        }

        .trial-info {
          background: rgba(0, 212, 255, 0.1);
          border: 1px solid rgba(0, 212, 255, 0.3);
          border-radius: 8px;
          padding: 0.75rem;
          margin-top: 0.5rem;
        }

        .next-steps {
          text-align: left;
          margin-bottom: 2rem;
        }

        .next-steps h3 {
          color: var(--text-white);
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          text-align: center;
        }

        .steps-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .step-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--glass-border);
          border-radius: 12px;
          transition: all 0.3s ease;
          opacity: 0.5;
        }

        .step-item.completed {
          opacity: 1;
          background: rgba(52, 199, 89, 0.1);
          border-color: rgba(52, 199, 89, 0.3);
        }

        .step-icon {
          width: 32px;
          height: 32px;
          background: var(--glass-bg);
          border: 1px solid var(--glass-border);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          color: var(--text-light);
          transition: all 0.3s ease;
        }

        .step-item.completed .step-icon {
          background: #34c759;
          border-color: #34c759;
          color: white;
        }

        .step-content h4 {
          color: var(--text-white);
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.25rem;
        }

        .step-content p {
          color: var(--text-light);
          font-size: 0.9rem;
          line-height: 1.4;
          margin: 0;
        }

        .thankyou-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .btn-primary, .btn-secondary {
          padding: 1rem 2rem;
          border: none;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .btn-primary {
          background: var(--accent-gradient);
          color: var(--text-white);
        }

        .btn-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #12c2e9, #c471ed, #ff6b9d);
          transition: left 0.3s ease;
          z-index: -1;
        }

        .btn-primary:hover::before {
          left: 0;
        }

        .btn-secondary {
          background: transparent;
          color: var(--text-light);
          border: 2px solid var(--glass-border);
        }

        .btn-secondary:hover {
          background: var(--glass-bg);
          color: var(--text-white);
          border-color: var(--cyberpunk-blue);
        }

        .auto-redirect-notice {
          color: var(--text-light);
          font-size: 0.9rem;
          opacity: 0.8;
          font-style: italic;
          margin: 0;
        }

        @media (max-width: 768px) {
          .thankyou-modal {
            padding: 2rem 1.5rem;
          }

          .success-checkmark {
            width: 60px;
            height: 60px;
            font-size: 2rem;
          }

          .ring {
            width: 60px;
            height: 60px;
          }

          .thankyou-title {
            font-size: 1.5rem;
          }

          .thankyou-actions {
            flex-direction: column;
          }

          .btn-primary, .btn-secondary {
            width: 100%;
          }
        }
      `}</style>
    </AnimatePresence>
  );
};

export default ThankYou;